'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';

interface NavItem {
    id: string;
    label: string;
    href: string;
    icon: string;
    badge?: string;
    badgeColor?: string;
}

interface NavGroup {
    label: string;
    items: NavItem[];
}

const DashboardSidebar = () => {
    const pathname = usePathname();
    const router = useRouter();
    const { user, signOut } = useAuth();
    const { closeMobileMenu, currentProject, setCurrentProject, projects, projectsLoading } = useNavigation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showProjectSwitcher, setShowProjectSwitcher] = useState(false);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    const handleLogout = async () => {
        await signOut();
        router.push('/');
        setShowUserMenu(false);
        setIsMobileMenuOpen(false);
    };

    const isActive = (href: string) => {
        if (href === '/overview') {
            return pathname === '/overview' || pathname === '/dashboard';
        }
        return pathname.startsWith(href);
    };

    const handleSelectProject = (project: typeof projects[number]) => {
        setCurrentProject(project);
        setShowProjectSwitcher(false);
        setIsMobileMenuOpen(false);
        router.push('/overview');
    };

    const scopedHref = (href: string) => {
        return href;
    };

    const closeMobileNav = () => {
        setIsMobileMenuOpen(false);
        closeMobileMenu();
    };

    const navGroups: NavGroup[] = [
        {
            label: 'Workspace',
            items: [
                {
                    id: 'overview',
                    label: 'Overview',
                    href: '/overview',
                    icon: 'HomeIcon',
                },
            ],
        },
        {
            label: 'Capture',
            items: [
                { id: 'leads', label: 'Leads', href: '/leads', icon: 'UsersIcon' },
                ...(currentProject?.project_type === 'widget_form'
                    ? [{ id: 'embed', label: 'Embed widget', href: '/embed', icon: 'CodeBracketIcon' }]
                    : []),
            ],
        },
        {
            label: 'Engage',
            items: [
                { id: 'emails', label: 'Email automations', href: '/emails', icon: 'EnvelopeIcon' },
                { id: 'broadcasts', label: 'Broadcasts', href: '/broadcasts', icon: 'MegaphoneIcon' },
                { id: 'gamification', label: 'Gamification', href: '/gamification', icon: 'TrophyIcon' },
            ],
        },
        {
            label: 'Launch',
            items: [
                ...(currentProject?.project_type === 'widget_form'
                    ? []
                    : [{ id: 'deployment', label: 'Deployment status', href: '/deployment', icon: 'CloudIcon' }]),
                { id: 'integrations', label: 'Integrations', href: '/integrations', icon: 'LinkIcon' },
            ],
        },
        {
            label: 'Learn',
            items: [
                { id: 'growth', label: 'Growth playbook', href: '/growth', icon: 'BookOpenIcon' },
                { id: 'community', label: 'Community', href: '/community', icon: 'ChatBubbleLeftRightIcon' },
            ],
        },
    ];

    return (
        <>
            {/* Mobile Header Bar */}
            <header className="md:hidden fixed top-[var(--banner-height,0px)] left-0 right-0 z-[100] bg-white/95 backdrop-blur-md border-b border-gray-100 transition-[top] duration-300">
                <div className="flex items-center h-14 px-4">
                    {/* Menu Toggle Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="flex items-center justify-center w-10 h-10 -ml-2 rounded-xl text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                        aria-label="Open menu"
                    >
                        <Icon name="Bars3Icon" size={24} />
                    </button>

                    {/* Logo - Left aligned next to menu */}
                    <Link
                        href={currentProject ? scopedHref('/overview') : '/start'}
                        className="flex items-center gap-2 ml-1"
                        onClick={closeMobileNav}
                    >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-violet-700 flex items-center justify-center shadow-sm">
                            <span className="text-white font-bold text-sm">W</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">Waitlistly</span>
                    </Link>
                </div>
            </header>

            {/* Mobile Slide-Out Sidebar */}
            <div
                className={`md:hidden fixed inset-0 z-[200] transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                style={{ top: 'var(--banner-height, 0px)' }}
            >
                {/* Backdrop Overlay */}
                <div
                    className={`absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
                        }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                />

                {/* Sidebar Panel */}
                <aside
                    className={`absolute top-0 left-0 bottom-0 w-[280px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                        }`}
                >
                    {/* Sidebar Header */}
                    <div className="flex items-center justify-between h-16 px-5 border-b border-gray-100 shrink-0">
                        <Link
                            href={currentProject ? scopedHref('/overview') : '/start'}
                            className="flex items-center gap-3"
                            onClick={closeMobileNav}
                        >
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-700 flex items-center justify-center shadow-sm shadow-violet-500/20">
                                <span className="text-white font-bold text-base">W</span>
                            </div>
                            <span className="text-[22px] font-bold tracking-tight text-gray-950">Waitlistly</span>
                        </Link>
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center justify-center w-9 h-9 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Close menu"
                        >
                            <Icon name="XMarkIcon" size={22} />
                        </button>
                    </div>

                    {/* Mobile Project Switcher */}
                    <div className="px-4 pt-4 pb-2">
                        <button
                            onClick={() => setShowProjectSwitcher(!showProjectSwitcher)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                        >
                            {currentProject ? (
                                <>
                                    <div className="w-7 h-7 rounded-lg bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
                                        <Icon name={currentProject.project_type === 'widget_form' ? 'CodeBracketIcon' : 'GlobeAltIcon'} size={15} className="text-violet-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{currentProject.name}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="w-7 h-7 rounded-lg bg-gray-50 ring-1 ring-gray-200 flex items-center justify-center shrink-0">
                                        <Icon name="RectangleStackIcon" size={15} className="text-gray-500" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-500">Select a project</span>
                                </>
                            )}
                            <Icon name="ChevronUpDownIcon" size={16} className="text-gray-400 shrink-0" />
                        </button>

                        {showProjectSwitcher && (
                            <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-[240px] flex flex-col">
                                <div className="overflow-y-auto flex-1 py-1">
                                    {projects.length === 0 ? (
                                        <div className="px-3 py-3 text-center text-sm text-gray-400">No projects yet</div>
                                    ) : (
                                        projects.map((project) => (
                                            <button
                                                key={project.id}
                                                onClick={() => handleSelectProject(project)}
                                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-violet-50 transition-colors ${currentProject?.id === project.id ? 'bg-violet-50' : ''}`}
                                            >
                                                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                                                    <Icon name={project.project_type === 'widget_form' ? 'CodeBracketIcon' : 'GlobeAltIcon'} size={14} className="text-violet-600" />
                                                </div>
                                                <p className="text-sm font-medium text-gray-900 truncate flex-1">{project.name}</p>
                                                {currentProject?.id === project.id && (
                                                    <Icon name="CheckIcon" size={16} className="text-violet-600 shrink-0" />
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                                <div className="border-t border-gray-100 py-1">
                                    <Link
                                        href="/create"
                                        onClick={() => { setShowProjectSwitcher(false); closeMobileNav(); }}
                                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-violet-600 hover:bg-violet-50 transition-colors"
                                    >
                                        <Icon name="PlusIcon" size={16} className="text-violet-600" />
                                        Create new project
                                    </Link>
                                    <Link
                                        href="/start"
                                        onClick={() => { setShowProjectSwitcher(false); closeMobileNav(); }}
                                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                    >
                                        <Icon name="Squares2X2Icon" size={16} className="text-gray-500" />
                                        View all projects
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Navigation Links - Scrollable */}
                    <nav className="px-3 py-4 flex-1 overflow-y-auto">
                        {navGroups.map((group) => (
                            <div key={group.label} className="mb-5 last:mb-0">
                                <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">
                                    {group.label}
                                </p>
                                <div className="space-y-1">
                                    {group.items.map((item) => {
                                        const active = isActive(item.href);
                                        return (
                                            <Link
                                                key={item.id}
                                                href={scopedHref(item.href)}
                                                onClick={() => {
                                                    closeMobileNav();
                                                }}
                                                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-200 ${active
                                                    ? 'bg-violet-50 text-violet-700'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100'
                                                    }`}
                                            >
                                                <div className={`flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${active
                                                    ? 'bg-violet-100'
                                                    : 'bg-gray-100 group-hover:bg-gray-200'
                                                    }`}>
                                                    <Icon
                                                        name={item.icon}
                                                        size={19}
                                                        className={active ? 'text-violet-600' : 'text-gray-500'}
                                                    />
                                                </div>
                                                <span className="flex-1">{item.label}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>

                    {/* Bottom Section - Fixed at bottom */}
                    <div className="shrink-0 border-t border-gray-100 bg-gray-50/50">
                        {/* Create Waitlist CTA */}
                        <div className="px-3 pt-3 pb-2">
                            <Link
                                href="/create"
                                onClick={closeMobileNav}
                                className="flex items-center justify-center gap-2 w-full py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:from-violet-500 hover:to-indigo-500 active:scale-[0.98] transition-all font-semibold text-[15px] shadow-lg shadow-violet-500/25"
                            >
                                <Icon name="PlusIcon" size={20} />
                                <span>Create Project</span>
                            </Link>
                        </div>

                        {/* User Section */}
                        <div className="px-3 pb-3 safe-area-inset-bottom">
                            {user && (
                                <div className="space-y-2">
                                    <Link
                                        href="/account"
                                        onClick={closeMobileNav}
                                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${pathname === '/account'
                                            ? 'bg-white shadow-sm text-violet-700'
                                            : 'text-gray-600 hover:bg-white hover:shadow-sm'
                                            }`}
                                    >
                                        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm">
                                            {user.email?.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                {user.email?.split('@')[0]}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <Icon name="ChevronRightIcon" size={18} className="text-gray-400" />
                                    </Link>

                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100">
                                            <Icon name="ArrowRightOnRectangleIcon" size={20} />
                                        </div>
                                        <span className="font-medium text-[15px]">Sign Out</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>

            {/* Desktop Sidebar */}
            <aside
                className={`hidden md:flex flex-col fixed left-0 top-[var(--banner-height,0px)] bottom-0 z-[100] bg-white border-r border-gray-200 transition-all duration-300 ${isCollapsed ? 'w-[72px]' : 'w-[280px]'
                    }`}
            >
                {/* Logo */}
                <div className={`flex items-center h-[76px] border-b border-gray-100 ${isCollapsed ? 'justify-center px-3' : 'px-6'}`}>
                    <Link
                        href={currentProject ? scopedHref('/overview') : '/start'}
                        className={`flex items-center hover:opacity-90 transition-opacity ${isCollapsed ? 'justify-center' : 'gap-3.5'}`}
                        onClick={closeMobileMenu}
                    >
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-700 flex items-center justify-center shadow-sm shadow-violet-500/20 shrink-0">
                            <span className="text-white font-bold text-base">W</span>
                        </div>
                        {!isCollapsed && (
                            <span className="text-[24px] font-bold tracking-tight text-gray-950">Waitlistly</span>
                        )}
                    </Link>
                </div>

                {/* Project Switcher */}
                {!isCollapsed && (
                    <div className="px-5 pt-4 pb-3">
                        <div className="relative">
                            <button
                                onClick={() => setShowProjectSwitcher(!showProjectSwitcher)}
                                className="group w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                            >
                                {currentProject ? (
                                    <>
                                        <div className="w-7 h-7 rounded-lg bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
                                            <Icon name={currentProject.project_type === 'widget_form' ? 'CodeBracketIcon' : 'GlobeAltIcon'} size={15} className="text-violet-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[14px] font-semibold text-gray-950 truncate">{currentProject.name}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-7 h-7 rounded-lg bg-gray-50 ring-1 ring-gray-200 flex items-center justify-center shrink-0">
                                            <Icon name="RectangleStackIcon" size={15} className="text-gray-500" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-500 truncate">Select a project</span>
                                    </>
                                )}
                                <Icon name="ChevronUpDownIcon" size={15} className="text-gray-400 shrink-0 transition-colors group-hover:text-gray-600" />
                            </button>

                            {showProjectSwitcher && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowProjectSwitcher(false)} />
                                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl shadow-gray-900/10 z-50 overflow-hidden max-h-[320px] flex flex-col">
                                        <div className="px-3 pt-3 pb-2 border-b border-gray-100">
                                            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">Switch project</p>
                                        </div>
                                        <div className="overflow-y-auto flex-1 py-1.5">
                                            {projectsLoading ? (
                                                <div className="px-3 py-4 text-center text-sm text-gray-400">Loading...</div>
                                            ) : projects.length === 0 ? (
                                                <div className="px-3 py-4 text-center text-sm text-gray-400">No projects yet</div>
                                            ) : (
                                                projects.map((project) => (
                                                    <button
                                                        key={project.id}
                                                        onClick={() => handleSelectProject(project)}
                                                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-violet-50 transition-colors ${currentProject?.id === project.id ? 'bg-violet-50' : ''}`}
                                                    >
                                                        <div className="w-7 h-7 rounded-lg bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
                                                            <Icon name={project.project_type === 'widget_form' ? 'CodeBracketIcon' : 'GlobeAltIcon'} size={14} className="text-violet-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                                                        </div>
                                                        {currentProject?.id === project.id && (
                                                            <Icon name="CheckIcon" size={16} className="text-violet-600 shrink-0" />
                                                        )}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                        <div className="border-t border-gray-100 py-1">
                                            <Link
                                                href="/create"
                                                onClick={() => { setShowProjectSwitcher(false); }}
                                                className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-violet-600 hover:bg-violet-50 transition-colors"
                                            >
                                                <div className="w-7 h-7 rounded-lg bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
                                                    <Icon name="PlusIcon" size={14} className="text-violet-600" />
                                                </div>
                                                Create new project
                                            </Link>
                                            <Link
                                                href="/start"
                                                onClick={() => { setShowProjectSwitcher(false); }}
                                                className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="w-7 h-7 rounded-lg bg-gray-50 ring-1 ring-gray-200 flex items-center justify-center shrink-0">
                                                    <Icon name="Squares2X2Icon" size={14} className="text-gray-500" />
                                                </div>
                                                View all projects
                                            </Link>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {isCollapsed && (
                    <div className="px-2 pt-4 pb-2 flex justify-center">
                        <button
                            onClick={() => {
                                router.push('/start');
                            }}
                            className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors"
                            title="Switch project"
                        >
                            <Icon name="RectangleStackIcon" size={18} className="text-gray-500" />
                        </button>
                    </div>
                )}

                {/* Navigation */}
                <nav className="px-3 py-4 overflow-y-auto">
                    {navGroups.map((group) => (
                        <div key={group.label} className="mb-5 last:mb-0">
                            {!isCollapsed && (
                                <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">
                                    {group.label}
                                </p>
                            )}
                            <div className="space-y-1">
                                {group.items.map((item) => {
                                    const active = isActive(item.href);
                                    return (
                                        <Link
                                            key={item.id}
                                            href={scopedHref(item.href)}
                                            onClick={() => {
                                                closeMobileMenu();
                                            }}
                                            className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-[15px] transition-all duration-150 ${active
                                                ? 'bg-violet-50 text-violet-700'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                } ${isCollapsed ? 'justify-center' : ''}`}
                                            title={isCollapsed ? item.label : undefined}
                                        >
                                            <Icon
                                                name={item.icon}
                                                size={20}
                                                className={`shrink-0 transition-colors ${active ? 'text-violet-600' : 'text-gray-400 group-hover:text-gray-600'
                                                    }`}
                                            />
                                            {!isCollapsed && <span className="flex-1">{item.label}</span>}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Collapse Toggle */}
                <div className={`px-3 pb-2 ${isCollapsed ? 'px-2' : ''}`}>
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-gray-400 hover:text-gray-500 transition-all text-xs ${isCollapsed ? 'justify-center' : ''
                            }`}
                        title={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                        <Icon
                            name={isCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
                            size={12}
                        />
                        {!isCollapsed && <span className="text-[11px]">Collapse</span>}
                    </button>
                </div>

                {/* Create New Button */}
                <div className={`px-3 py-3 ${isCollapsed ? 'px-2' : ''}`}>
                    <Link
                        href="/create"
                        className={`flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg hover:from-violet-500 hover:to-indigo-500 transition-all font-medium text-sm shadow-sm ${isCollapsed ? 'px-2' : 'px-4'
                            }`}
                        title={isCollapsed ? 'Create Project' : undefined}
                    >
                        <Icon name="PlusIcon" size={18} />
                        {!isCollapsed && <span>Create Project</span>}
                    </Link>
                </div>

                {/* User Section */}
                <div className={`border-t border-gray-100 p-3 ${isCollapsed ? 'px-2' : ''}`}>
                    {user && (
                        <div className="relative">
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className={`w-full flex items-center gap-3 p-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-all ${isCollapsed ? 'justify-center' : ''
                                    }`}
                                title={isCollapsed ? user.email || 'User menu' : undefined}
                            >
                                <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-violet-700 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                    {user.email?.charAt(0).toUpperCase()}
                                </div>
                                {!isCollapsed && (
                                    <>
                                        <div className="flex-1 text-left min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {user.email?.split('@')[0]}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <Icon name="ChevronUpIcon" size={16} className="text-gray-400 shrink-0" />
                                    </>
                                )}
                            </button>

                            {showUserMenu && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                                    <div className={`absolute bottom-full mb-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden ${isCollapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'
                                        }`}>
                                        <Link
                                            href="/account"
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <Icon name="UserCircleIcon" size={18} />
                                            <span>Profile</span>
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                                        >
                                            <Icon name="ArrowRightOnRectangleIcon" size={18} />
                                            <span>Sign Out</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
};

export default DashboardSidebar;
